import { ColumnDef } from '@tanstack/react-table';
import { SourcingFunnelDetails } from 'api-clients/monolith';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useNoHiringGoalDialog } from '@/contexts/no-hiring-goal-dialog-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';

export function useSourcingOpeningsColumns(): ColumnDef<SourcingFunnelDetails>[] {
  const intl = useIntl();
  const { dashboardPath } = useNavigationPaths();
  const { openNoHiringGoalDialog } = useNoHiringGoalDialog();
  const navigate = useNavigate();

  const handleNoHiringGoal = (rowItem: SourcingFunnelDetails) => {
    // TODO: add locationId and funnelId to the API response
    return openNoHiringGoalDialog({
      title: rowItem.name,
    });
  };

  const handleOpeningClick = (rowItem: SourcingFunnelDetails) => {
    if (!rowItem.active_hiring_goal) return handleNoHiringGoal(rowItem);

    return navigate(`${dashboardPath}/${rowItem.slug}/channels`);
  };

  return [
    {
      accessorKey: 'name',
      cell: ({ row }) => {
        return (
          <div className="w-[220px] overflow-hidden">
            {row.original.name === 'Sourcing Openings' ? (
              <Typography variant="h6">
                {intl.formatMessage({
                  defaultMessage: 'Sourcing Openings',
                  description:
                    'Section title for the aggregate row in the openings table',
                })}
              </Typography>
            ) : (
              <Button
                variant="link"
                onClick={() => handleOpeningClick(row.original)}
                className="text-h6 text-fx-primary h-auto text-left font-medium whitespace-break-spaces"
              >
                {row.original.name}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'spend',
      cell: ({ row }) => {
        return (
          <>
            {/* Display caption only for the aggregate data */}
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Spend',
                  description:
                    'Sourcing table aggregate row: budget spent caption',
                })}
              </Typography>
            )}
            <Typography variant="large2">
              {row.original.spend ?? '-'}
            </Typography>
          </>
        );
      },
    },
    {
      accessorKey: 'duration',
      cell: ({ row }) => {
        return (
          <>
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Duration (days)',
                  description:
                    'Sourcing table aggregate row: how long the spend period runs',
                })}
              </Typography>
            )}
            <Typography variant="large2">
              {row.original.duration ?? '-'}
            </Typography>
          </>
        );
      },
    },
    {
      accessorKey: 'applied_count',
      cell: ({ row }) => {
        return (
          <>
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Applicants',
                  description:
                    'Sourcing table aggregate row: count of people who applied',
                })}
              </Typography>
            )}
            <Typography variant="large2">
              {row.original.applied_count ?? '-'}
            </Typography>
          </>
        );
      },
    },
    {
      accessorKey: 'cpa',
      cell: ({ row }) => {
        return (
          <>
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'CPA',
                  description:
                    'Sourcing table aggregate row: cost per applicant (acronym kept short)',
                })}
              </Typography>
            )}
            <Typography variant="large2">{row.original.cpa ?? '-'}</Typography>
          </>
        );
      },
    },
    {
      accessorKey: 'conversion',
      cell: ({ row }) => {
        return (
          <>
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Conversion',
                  description:
                    'Sourcing table aggregate row: conversion rate as a percent',
                })}
              </Typography>
            )}
            <Typography variant="large2">
              {row.original.conversion ? `${row.original.conversion}%` : '-'}
            </Typography>
          </>
        );
      },
    },
    {
      accessorKey: 'hired_count',
      cell: ({ row }) => {
        return (
          <>
            {row.original.name === 'Sourcing Openings' && (
              <Typography variant="caption">
                {intl.formatMessage({
                  defaultMessage: 'Hires',
                  description:
                    'Sourcing table aggregate row: people hired in the period',
                })}
              </Typography>
            )}
            <Typography variant="large2">
              {row.original.hired_count ?? '-'}
            </Typography>
          </>
        );
      },
    },
  ];
}
